import { useMemo } from 'react'
import * as THREE from 'three'
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js'
import {
  corteDaCisterna,
  ESPESSURA_PAREDE_CISTERNA_M,
  formaDaCisterna,
  ilhasDoPerfil,
  parametrosDoObjeto,
  perfilDoTerreno,
  type FormaCisterna,
  type ObjetoTabletop,
  type PerfilTerreno,
} from '../../dominio/tabletop'

/**
 * Peças de terreno (talude, barranco, vala, canal, cisterna): o perfil do
 * domínio, extrudado ao longo de X (a cisterna é redonda). Como a altura do
 * chão é calculada a partir do MESMO perfil (`dominio/tabletop/terreno.ts`), o
 * que se vê é o que os objetos pisam.
 */

const COR_TERRENO: Readonly<Record<string, string>> = {
  talude: '#6f8f4e',
  barranco: '#9a7350',
  vala: '#7d6445',
  canal: '#9aa0a2',
  cisterna: '#8d9396',
}
const COR_AGUA = '#5b8a78'

/**
 * Sólido do terreno: cada "ilha" do perfil (trecho que sobe do chão e volta a
 * ele) vira um prisma de `2 × semiLarguraM` de extensão em X, centrado na
 * origem da peça. Ilhas à parte porque um perfil com vão no chão (vala, canal)
 * sobreporia a base e quebraria a malha.
 */
export function geometriaDoTerreno(perfil: PerfilTerreno): THREE.BufferGeometry {
  const { pontos, semiLarguraM } = perfil
  const prismas = ilhasDoPerfil(pontos).map((ilha) => {
    const forma = new THREE.Shape()
    const primeiro = ilha[0]!
    const ultimo = ilha[ilha.length - 1]!
    forma.moveTo(primeiro[0], 0)
    for (const [z, y] of ilha) forma.lineTo(z, y)
    forma.lineTo(ultimo[0], 0)
    forma.closePath()
    return new THREE.ExtrudeGeometry(forma, { depth: semiLarguraM * 2, bevelEnabled: false })
  })
  const geometria = mergeGeometries(prismas, false) ?? new THREE.BufferGeometry()
  prismas.forEach((g) => g.dispose())
  // a forma está no plano (z, altura) e é extrudada em +Z; gira para extrudar ao longo de X
  geometria.rotateY(-Math.PI / 2)
  geometria.translate(semiLarguraM, 0, 0)
  return geometria
}

/** Tira o `uv` e deixa tudo sem índice, para as peças de tipos diferentes se juntarem numa malha só. */
function simples(g: THREE.BufferGeometry): THREE.BufferGeometry {
  const sem = g.index ? g.toNonIndexed() : g
  sem.deleteAttribute('uv')
  return sem
}

/**
 * O canal escavado: a linha do vão (parede, banqueta, leito, parede) extrudada
 * ao longo de X como uma fita com as normais para dentro do vão, as duas
 * tampas nas pontas do canal e as muretas por cima da rua. A origem é o centro
 * do leito, no nível da rua.
 */
export function geometriaDoCanal(perfil: PerfilTerreno): THREE.BufferGeometry {
  const { vazio = [], muretas = [], semiLarguraM: s } = perfil
  const pecas: THREE.BufferGeometry[] = []

  // a fita: cada trecho do perfil vira um retângulo; a ordem dos vértices deixa a normal para dentro do vão
  const posicoes: number[] = []
  for (let i = 1; i < vazio.length; i += 1) {
    const [z0, y0] = vazio[i - 1]!
    const [z1, y1] = vazio[i]!
    // (-s, y0, z0), (-s, y1, z1), (+s, y1, z1), (+s, y0, z0)
    posicoes.push(-s, y0, z0, -s, y1, z1, s, y1, z1, -s, y0, z0, s, y1, z1, s, y0, z0)
  }
  const fita = new THREE.BufferGeometry()
  fita.setAttribute('position', new THREE.Float32BufferAttribute(posicoes, 3))
  fita.computeVertexNormals()
  pecas.push(fita)

  // as tampas: o corte do canal, em cada ponta
  if (vazio.length >= 3) {
    const forma = new THREE.Shape()
    forma.moveTo(vazio[0]![0], vazio[0]![1])
    for (const [z, y] of vazio) forma.lineTo(z, y)
    forma.closePath()
    for (const lado of [-1, 1]) {
      const tampa = simples(new THREE.ShapeGeometry(forma))
      // a forma está no plano (z, altura): gira para o plano YZ e leva para a ponta
      tampa.rotateY(-Math.PI / 2)
      tampa.translate(lado * s, 0, 0)
      pecas.push(tampa)
    }
  }

  for (const { z0, z1, alturaM } of muretas) {
    const caixa = simples(new THREE.BoxGeometry(s * 2, alturaM, z1 - z0))
    caixa.translate(0, alturaM / 2, (z0 + z1) / 2)
    pecas.push(caixa)
  }
  const geometria = mergeGeometries(pecas, false) ?? new THREE.BufferGeometry()
  pecas.forEach((g) => g.dispose())
  return geometria
}

/**
 * A cisterna escavada: a parede do poço (um cilindro aberto, da boca ao fundo)
 * e o piso. A boca é o furo que a cena abre no chão da rua.
 */
export function geometriaDaParedeDaCisterna(forma: FormaCisterna): THREE.BufferGeometry {
  const parede = new THREE.CylinderGeometry(forma.raioFuroM, forma.raioFuroM, forma.profundidadeM, 48, 1, true)
  parede.translate(0, -forma.profundidadeM / 2, 0)
  return parede
}

function Canal({ objeto, perfil }: { objeto: ObjetoTabletop; perfil: PerfilTerreno }) {
  const p = parametrosDoObjeto(objeto)
  const comprimento = p.comprimentoM!
  const leito = p.larguraLeitoM!
  const nivel = p.nivelAguaM!
  const yLeito = -p.alturaParedeM!
  const setas = p.correnteza! >= 0.5 && nivel >= 0.05
  const geometria = useMemo(() => geometriaDoCanal(perfil), [perfil])
  const arestas = useMemo(() => new THREE.EdgesGeometry(geometria, 25), [geometria])
  const quantidadeSetas = Math.max(2, Math.floor(comprimento / 8))
  return (
    <group>
      <mesh geometry={geometria} userData={{ terreno: true }}>
        <meshStandardMaterial color={COR_TERRENO.canal} roughness={0.95} side={THREE.DoubleSide} />
      </mesh>
      <lineSegments geometry={arestas} raycast={() => null}>
        <lineBasicMaterial color="#4b5052" transparent opacity={0.6} />
      </lineSegments>
      {nivel >= 0.02 && (
        <mesh position={[0, yLeito + nivel / 2 + 0.02, 0]} raycast={() => null}>
          <boxGeometry args={[comprimento, nivel, leito]} />
          <meshStandardMaterial color={COR_AGUA} transparent opacity={0.55} roughness={0.2} depthWrite={false} />
        </mesh>
      )}
      {setas &&
        Array.from({ length: quantidadeSetas }, (_, i) => (
          <mesh
            key={i}
            raycast={() => null}
            position={[-comprimento / 2 + (comprimento / quantidadeSetas) * (i + 0.5), yLeito + nivel + 0.05, 0]}
            rotation={[0, 0, -Math.PI / 2]}
          >
            <coneGeometry args={[0.35, 1.1, 12]} />
            <meshStandardMaterial color="#e0f2f1" transparent opacity={0.8} />
          </mesh>
        ))}
    </group>
  )
}

/**
 * A metade de trás da parede (z ≤ 0 do objeto): com a da frente aberta, vê-se
 * o lado de dentro do poço. O cilindro do three.js mede o ângulo a partir de
 * +Z; de π/2 a 3π/2 fica a metade de trás.
 */
export function geometriaDaMeiaParede(raioM: number, profundidadeM: number): THREE.BufferGeometry {
  const g = new THREE.CylinderGeometry(raioM, raioM, profundidadeM, 32, 1, true, Math.PI / 2, Math.PI)
  g.translate(0, -profundidadeM / 2, 0)
  return g
}

/**
 * A cisterna em corte: a metade de trás da parede (por dentro e por fora), o
 * piso, a espessura da parede nas duas bordas do corte e a terra em volta e
 * abaixo, desenhadas no plano do corte (z = 0), como num desenho técnico.
 */
function CisternaEmCorte({ forma }: { forma: FormaCisterna }) {
  const R = forma.raioFuroM
  const P = forma.profundidadeM
  const e = ESPESSURA_PAREDE_CISTERNA_M
  const corte = corteDaCisterna(forma)
  const W = corte.meiaLarguraM
  const F = corte.fundoM
  const L = corte.comprimentoM
  const dentro = useMemo(() => geometriaDaMeiaParede(R, P), [R, P])
  const fora = useMemo(() => geometriaDaMeiaParede(R + e, P + e), [R, P, e])
  // a terra só se vê pelo lado de dentro do corte (FrontSide): de fora ou de baixo do chão ela some e não tampa o poço
  const terra = <meshStandardMaterial color="#6b4f35" roughness={1} side={THREE.FrontSide} />
  const concreto = <meshStandardMaterial color="#a3a9ac" roughness={0.9} side={THREE.DoubleSide} />
  return (
    <group>
      <mesh geometry={dentro} raycast={() => null} data-meia-parede>
        <meshStandardMaterial color={COR_TERRENO.cisterna} roughness={0.95} side={THREE.DoubleSide} />
      </mesh>
      <mesh geometry={fora} raycast={() => null}>
        <meshStandardMaterial color="#7d8386" roughness={1} side={THREE.DoubleSide} />
      </mesh>
      {/* no plano do corte: a espessura da parede nas bordas e a laje do fundo */}
      {[-1, 1].map((lado) => (
        <mesh key={lado} position={[lado * (R + e / 2), -P / 2, 0.002]} raycast={() => null} data-corte="parede">
          <planeGeometry args={[e, P]} />
          {concreto}
        </mesh>
      ))}
      <mesh position={[0, -P - e / 2, 0.002]} raycast={() => null} data-corte="laje">
        <planeGeometry args={[2 * (R + e), e]} />
        {concreto}
      </mesh>
      {/* no plano do corte: a terra dos dois lados da parede e embaixo da laje */}
      {[-1, 1].map((lado) => (
        <mesh key={`t${lado}`} position={[lado * ((R + e + W) / 2), -F / 2, 0]} raycast={() => null} data-corte="terra">
          <planeGeometry args={[W - R - e, F]} />
          {terra}
        </mesh>
      ))}
      <mesh position={[0, -(P + e + F) / 2, 0]} raycast={() => null} data-corte="terra">
        <planeGeometry args={[2 * (R + e), F - P - e]} />
        {terra}
      </mesh>
      {/* a vala de inspeção na frente do corte: as duas paredes de terra e o fundo */}
      {[-1, 1].map((lado) => (
        // a face de cada parede da vala olha para dentro: a da esquerda para +X, a da direita para -X
        <mesh key={`v${lado}`} position={[lado * W, -F / 2, L / 2]} rotation={[0, (-lado * Math.PI) / 2, 0]} raycast={() => null} data-corte="vala">
          <planeGeometry args={[L, F]} />
          {terra}
        </mesh>
      ))}
      <mesh position={[0, -F, L / 2]} rotation={[-Math.PI / 2, 0, 0]} userData={{ terreno: true }} data-corte="vala">
        <planeGeometry args={[2 * W, L]} />
        <meshStandardMaterial color="#5b4430" roughness={1} />
      </mesh>
    </group>
  )
}

function Cisterna({ forma }: { forma: FormaCisterna }) {
  const parede = useMemo(() => geometriaDaParedeDaCisterna(forma), [forma])
  const transparente = forma.paredesTransparentes
  return (
    <group>
      {forma.meiaAberta ? <CisternaEmCorte forma={forma} /> : (
      <mesh geometry={parede} raycast={() => null}>
        <meshStandardMaterial
          color={COR_TERRENO.cisterna}
          roughness={0.95}
          side={THREE.DoubleSide}
          transparent={transparente}
          opacity={transparente ? 0.32 : 1}
          depthWrite={!transparente}
        />
      </mesh>
      )}
      {/* piso do poço */}
      <mesh position={[0, -forma.profundidadeM + 0.01, 0]} rotation={[-Math.PI / 2, 0, 0]} userData={{ terreno: true }}>
        <circleGeometry args={[forma.raioFuroM, 48]} />
        <meshStandardMaterial color="#5f6568" roughness={1} />
      </mesh>
      {/* anel de concreto na boca */}
      <mesh position={[0, 0.012, 0]} rotation={[-Math.PI / 2, 0, 0]} raycast={() => null}>
        <ringGeometry args={[forma.raioFuroM, forma.raioFuroM + 0.3, 48]} />
        <meshStandardMaterial color="#8d9396" roughness={0.9} />
      </mesh>
      {forma.comTampa && (
        <mesh position={[0, 0.05, 0]} raycast={() => null}>
          <cylinderGeometry args={[forma.raioFuroM * 1.05, forma.raioFuroM * 1.05, 0.1, 48]} />
          <meshStandardMaterial color="#64748b" metalness={0.6} roughness={0.5} />
        </mesh>
      )}
    </group>
  )
}

export function TerrenoTabletop({ objeto }: { objeto: ObjetoTabletop }) {
  const forma = formaDaCisterna(objeto)
  const perfil = forma ? null : perfilDoTerreno(objeto)
  const parametros = objeto.parametros
  const solido = useMemo(
    () => (perfil && !perfil.escavado ? geometriaDoTerreno(perfil) : null),
    // as medidas decidem a forma; `perfil` é recalculado a cada render
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [objeto.tipo, parametros],
  )
  const arestas = useMemo(() => (solido ? new THREE.EdgesGeometry(solido, 25) : null), [solido])

  if (forma) return <Cisterna forma={forma} />
  if (perfil?.escavado) return <Canal objeto={objeto} perfil={perfil} />
  if (!solido || !arestas) return null
  return (
    // `userData.terreno` deixa a cena achar a superfície sob o toque (colocar peça sobre a rampa)
    <group userData={{ terreno: true }}>
      <mesh geometry={solido} userData={{ terreno: true }}>
        <meshStandardMaterial color={objeto.cor ?? COR_TERRENO[objeto.tipo] ?? '#8b7355'} roughness={1} />
      </mesh>
      <lineSegments geometry={arestas} raycast={() => null}>
        <lineBasicMaterial color="#3f3a2b" transparent opacity={0.55} />
      </lineSegments>
      {perfil && (parametros?.pistaNoTopo ?? 0) >= 1 && <PistaNoTopo perfil={perfil} />}
    </group>
  )
}

/**
 * Pista de asfalto sobre o topo plano de um barranco ou talude: a rua corre ao
 * longo de X, com a faixa amarela tracejada no meio e a branca junto da borda.
 * É só pintura: o chão continua sendo o topo da peça.
 */
function PistaNoTopo({ perfil }: { perfil: NonNullable<ReturnType<typeof perfilDoTerreno>> }) {
  const pontos = perfil.pontos
  const fim = pontos[pontos.length - 1]!
  const inicio = pontos.find((p) => Math.abs(p[1] - fim[1]) < 1e-6)
  if (!inicio || fim[0] - inicio[0] < 1) return null
  const y = fim[1] + 0.012
  const largura = perfil.semiLarguraM * 2
  const zMeio = (inicio[0] + fim[0]) / 2
  const profundidade = fim[0] - inicio[0]
  const tracos = Math.max(1, Math.floor(largura / 4))
  return (
    <group raycast={() => null} data-pista-no-topo>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, y, zMeio]} raycast={() => null}>
        <planeGeometry args={[largura, profundidade]} />
        <meshStandardMaterial color="#2b2f36" roughness={0.95} />
      </mesh>
      {/* faixa branca a 30 cm da borda */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, y + 0.002, inicio[0] + 0.3]} raycast={() => null}>
        <planeGeometry args={[largura, 0.12]} />
        <meshStandardMaterial color="#f8fafc" roughness={0.8} />
      </mesh>
      {Array.from({ length: tracos }, (_, i) => -largura / 2 + (i + 0.5) * (largura / tracos)).map((x) => (
        <mesh key={x} rotation={[-Math.PI / 2, 0, 0]} position={[x, y + 0.002, zMeio]} raycast={() => null}>
          <planeGeometry args={[2, 0.12]} />
          <meshStandardMaterial color="#facc15" roughness={0.8} />
        </mesh>
      ))}
    </group>
  )
}
